import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const GitGenesLanding = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  
  const stats = [
    { number: "50K+", label: "Researchers" },
    { number: "1M+", label: "Sequences Analyzed" },
    { number: "180+", label: "Countries" }
  ];

  const tools = [
    { name: "GenBank", desc: "Modern interface, instant results", status: "Live" },
    { name: "BLAST", desc: "AI-powered sequence analysis", status: "Beta" },
    { name: "Alignment", desc: "Beautiful visualizations", status: "Soon" }
  ];

  return (
    <div className="bg-white">
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-xl z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                GitGenes
              </span>
              <span className="text-2xl font-bold text-emerald-600">.tech</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#story" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Story</a>
              <a href="#platform" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Platform</a>
              <a href="#community" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Community</a>
              <button className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all duration-300">
                Start Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.section 
        className="pt-24 pb-20 bg-gradient-to-br from-gray-50 to-white overflow-hidden"
        style={{ y }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200 rounded-full text-blue-700 text-sm font-medium mb-8">
                🧬 The Future of Bioinformatics
              </span>
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Git for{' '}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-emerald-600 bg-clip-text text-transparent">
                Genomics
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Born from a 2:47 AM frustration with crashed GenBank queries. 
              Now trusted by researchers worldwide for beautiful, fast, collaborative bioinformatics.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <button className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                Explore Platform
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold text-lg hover:border-gray-400 transition-all duration-300">
                Read Our Story
              </button>
            </motion.div>
            
            {/* Stats */}
            <motion.div 
              className="grid grid-cols-3 gap-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* The Story */}
      <section id="story" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">The Origin Story</h2>
            <p className="text-xl text-gray-600">From late-night lab frustrations to revolutionary platform</p>
          </div>

          <div className="space-y-16">
            {/* Chapter 1 */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Problem That Wouldn't Go Away</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  It was 2:47 AM in the bioinformatics lab. Dr. Alex Chen stared at yet another crashed GenBank query, 
                  the third one that night. Around the lab, fellow researchers were wrestling with the same demons: 
                  outdated interfaces, inconsistent data formats, and tools that seemed designed in the early 2000s.
                </p>
                <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700">
                  "There has to be a better way"
                </blockquote>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-8 border border-blue-100">
                <div className="font-mono text-sm text-gray-700">
                  <div className="text-red-500 mb-2">ERROR: Connection timeout</div>
                  <div className="text-red-500 mb-2">Query failed after 45 minutes</div>
                  <div className="text-gray-500">GenBank server unresponsive...</div>
                  <div className="mt-4 text-blue-600">// This was the moment everything changed</div>
                </div>
              </div>
            </motion.div>

            {/* Chapter 2 */}
            <motion.div
              className="grid md:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="order-2 md:order-1">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">The Midnight Epiphany</h4>
                  <div className="text-center py-8">
                    <div className="text-4xl font-bold text-emerald-600">Git + Genes</div>
                    <div className="text-2xl text-gray-600 mt-2">=</div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent mt-2">
                      GitGenes
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">The Midnight Epiphany</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  The idea struck like lightning: what if biological data could be as elegant and version-controlled as code? 
                  What if researchers could "commit" their sequence analyses like developers commit code?
                </p>
                <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-gray-700">
                  "Every biologist should feel like a developer. Every analysis should be reproducible, shareable, and beautiful."
                </blockquote>
              </div>
            </motion.div>

            {/* Chapter 3 */}
            <motion.div
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Community Rallies</h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                Word spread through Twitter, Reddit, and lab Slack channels. When colleagues saw the first GitGenes tool, 
                their reactions were immediate and passionate.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  "This is what GenBank should have been!",
                  "Finally, a tool that doesn't look like it's from 1995!",
                  "This saved me 3 hours today!"
                ].map((quote, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <p className="italic text-gray-700">"{quote}"</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Preview */}
      <section id="platform" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">The Platform Today</h2>
            <p className="text-xl text-gray-600">Simple promise, powerful execution</p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Version Control for <span className="text-blue-600">Biological Data</span>
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                What started as a simple GenBank interface has become something bigger—a movement toward better scientific software. 
                Tools that researchers actually want to use.
              </p>
              <div className="space-y-4">
                {tools.map((tool, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                    <div>
                      <div className="font-semibold text-gray-900">{tool.name}</div>
                      <div className="text-gray-600 text-sm">{tool.desc}</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tool.status === 'Live' ? 'bg-green-100 text-green-700' : 
                      tool.status === 'Beta' ? 'bg-blue-100 text-blue-700' : 
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {tool.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-800 px-6 py-3 flex items-center">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="flex-1 text-center text-white text-sm">genbank.gitgenes.tech</div>
                </div>
                <div className="p-6">
                  <div className="flex gap-3 mb-6">
                    <input 
                      type="text" 
                      placeholder="Search sequences, genes, proteins..."
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500"
                    />
                    <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold">
                      Search
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-gray-900">Homo sapiens BRCA1</span>
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">98.7%</span>
                      </div>
                      <div className="text-gray-500 text-sm font-mono">ATGGATTTCCGTCTGAG...</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community */}
      <section id="community" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Join the Movement</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            From Harvard to CERN, researchers worldwide are choosing GitGenes for their daily bioinformatics work. 
                        Be part of the movement making science more beautiful, accessible, and collaborative.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: "💬", title: "Discord Community", desc: "25K+ active researchers" },
              { icon: "📚", title: "Open Source", desc: "500+ contributors on GitHub" },
              { icon: "🎓", title: "Research Impact", desc: "1,200+ citations in papers" }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300">
              Join Discord
            </button>
            <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold text-lg hover:border-gray-400 transition-all duration-300">
              Contribute on GitHub
            </button>
          </div>
        </div>
      </section>

      {/* The Future */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-violet-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">The Future</h2>
            <p className="text-xl text-gray-600 mb-12">
              That original napkin still hangs framed in the GitGenes office. 
              It's a reminder that the best solutions often come from the simplest frustrations.
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The GitGenes Manifesto</h3>
              <blockquote className="text-lg text-gray-700 italic leading-relaxed">
                "Every sequence tells a story. Every analysis should be reproducible. 
                Every researcher deserves tools as elegant as their hypotheses. 
                We're not just building software—we're building the future of biological discovery."
              </blockquote>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="text-left">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">2025 Goals</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Launch 5 new analysis tools</li>
                  <li>• AI-powered research assistant</li>
                  <li>• Collaborative workspaces</li>
                  <li>• Mobile companion apps</li>
                </ul>
              </div>
              <div className="text-left">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Big Dreams</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Academic partnerships worldwide</li>
                  <li>• Research paper integration</li>
                  <li>• Conference presentations</li>
                  <li>• Open-source ecosystem</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-8">
              Ready to Experience <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">Better Science</span>?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Join the revolution in bioinformatics. Because your research deserves better than 1995.
            </p>
            
            <button className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-12 py-6 rounded-full font-bold text-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 mb-8">
              Start Your Journey
            </button>
            
            <div className="text-gray-500 text-sm">
              Free to start • No credit card required • Join 50,000+ researchers
            </div>
          </motion.div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                  GitGenes
                </span>
                <span className="text-2xl font-bold text-emerald-600">.tech</span>
              </div>
              <p className="text-gray-600 mb-6 max-w-md">
                Making bioinformatics beautiful, accessible, and collaborative for researchers worldwide.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-600">
                <li><a href="#" className="hover:text-gray-900 transition-colors">GenBank Interface</a></li>
                <li><a href="#" className="hover:text-gray-900 transition-colors">BLAST Tools</a></li>
                <li><a href="#" className="hover:text-gray-900 transition-colors">Sequence Alignment</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Community</h4>
              <ul className="space-y-2 text-gray-600">
                <li><a href="#" className="hover:text-gray-900 transition-colors">Discord</a></li>
                <li><a href="#" className="hover:text-gray-900 transition-colors">GitHub</a></li>
                <li><a href="#" className="hover:text-gray-900 transition-colors">Documentation</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 mb-4 md:mb-0">
              &copy; 2024 GitGenes.tech. Built with ❤️ for science.
            </p>
            <div className="flex space-x-6 text-gray-500">
              <a href="#" className="hover:text-gray-700 transition-colors">Privacy</a>
              <a href="#" className="hover:text-gray-700 transition-colors">Terms</a>
              <a href="#" className="hover:text-gray-700 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GitGenesLanding;